import React, { useState } from 'react';
import SEO from '../components/SEO';

const SITE_DOMAIN = process.env.REACT_APP_SITE_DOMAIN || 'https://yourjsontools.com';

const TemplatePromptsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const categories = [
    { id: 'all', name: 'All', icon: '📋' },
    { id: 'coding', name: 'Coding', icon: '💻' },
    { id: 'qa', name: 'QA & Testing', icon: '🧪' },
    { id: 'writing', name: 'Writing', icon: '✍️' },
    { id: 'analysis', name: 'Analysis', icon: '📊' },
    { id: 'creative', name: 'Creative', icon: '🎨' },
    { id: 'business', name: 'Business', icon: '💼' }
  ];

  const prompts = [
    {
      id: 23,
      category: 'qa',
      title: 'JSON to BDD CDC Test Converter',
      description: 'Convert JSON response to Gherkin BDD format for CDC testing',
      prompt: `Convert the JSON value given to you into the BDD format I wrote for use in CDC tests.

**Rules:**
1. The key-type data set will be located under this sentence: 
   \`Then I do CDC test for response values with following data:\`
   with columns: \`| key | type |\`

2. Write the key and type values for EVERY field including nested objects and arrays.

3. Write the parameter count values using these sentences:
   - \`Then I expect to see response parameter count is 'X'\` (for root level)
   - \`Then I expect to see 'path' parameter children count is 'X' for CDC testing\` (for nested)

4. Do not use any sentences other than the BDD sentences provided!

5. Write the sentence inside a code block with Gherkin format.

6. Type mappings:
   - Objects → \`Object\`
   - Arrays → \`Array\`
   - Strings → \`String\`
   - Numbers (integer) → \`Integer\`
   - Numbers (decimal) → \`Double\`
   - Booleans → \`Boolean\`
   - Null → \`Null\`

7. For arrays, also include indexed items like \`details[0]\`, \`details[0].field\`

**Example:**

Input JSON:
\`\`\`json
{
  "notification": {
    "title": "İşlem Başarısız",
    "text": "Eksik veya hatalı bilgi girdiniz.",
    "type": "red",
    "showType": 1
  },
  "message": "Eksik veya hatalı bilgi girdiniz.",
  "details": [
    {
      "field": "phonenumber",
      "message": ["'Phone Number' boş olmamalı.", "'Phone Number' boş olamaz."]
    }
  ]
}
\`\`\`

Output:
\`\`\`gherkin
Then I do CDC test for response values with following data:
  | key                      | type    |
  | notification             | Object  |
  | notification.title       | String  |
  | notification.text        | String  |
  | notification.type        | String  |
  | notification.showType    | Integer |
  | message                  | String  |
  | details                  | Array   |
  | details[0]               | Object  |
  | details[0].field         | String  |
  | details[0].message       | Array   |
  | details[0].message[0]    | String  |
  | details[0].message[1]    | String  |
Then I expect to see response parameter count is '3'
Then I expect to see 'notification' parameter children count is '4' for CDC testing
Then I expect to see 'details[0]' parameter children count is '2' for CDC testing
Then I expect to see 'details[0].message' parameter children count is '2' for CDC testing
\`\`\`

---

**Now convert this JSON to BDD CDC format:**

\`\`\`json
[PASTE YOUR JSON RESPONSE HERE]
\`\`\``,
      tags: ['bdd', 'cdc', 'gherkin', 'cucumber', 'contract testing', 'qa']
    },
    {
      id: 13,
      category: 'qa',
      title: 'Selenium POM Automation Scenario',
      description: 'Generate Selenium test automation with Page Object Model pattern',
      prompt: `Write a complete Selenium automation test scenario using Page Object Model (POM) design pattern.

**Application Details:**
- Application URL: [URL]
- Application Type: [WEB/MOBILE WEB]
- Browser: [CHROME/FIREFOX/EDGE/SAFARI]

**Test Scenario:**
[DESCRIBE THE USER FLOW TO AUTOMATE]
Example: User logs in, navigates to dashboard, creates a new item, verifies success message

**Page Elements to Interact:**
[LIST THE UI ELEMENTS - buttons, inputs, dropdowns, etc.]

**Programming Language:** [JAVA/PYTHON/C#/JAVASCRIPT]
**Test Framework:** [TESTNG/JUNIT/PYTEST/NUNIT/MOCHA]

Please provide:

1. **Page Object Classes:**
   - BasePage with common methods (wait, click, sendKeys, etc.)
   - Separate page class for each page in the flow
   - Proper locator strategies (ID, CSS, XPath)
   - Encapsulated web elements with meaningful names

2. **Test Class:**
   - Test setup and teardown methods
   - Test method with clear assertions
   - Proper test annotations
   - Data-driven approach if applicable

3. **Utility Classes:**
   - WebDriver factory/manager
   - Configuration reader
   - Screenshot utility for failures
   - Wait utilities

4. **Best Practices Applied:**
   - Explicit waits instead of implicit
   - Fluent interface pattern for method chaining
   - Meaningful method and variable names
   - Comments explaining complex logic

5. **Sample Test Data:**
   - Example test data for the scenario
   - How to externalize test data (properties/JSON/Excel)`,
      tags: ['selenium', 'automation', 'pom', 'testing', 'qa']
    },
    {
      id: 14,
      category: 'qa',
      title: 'Git Branch Diff to Test Cases',
      description: 'Analyze code changes between branches and generate test cases',
      prompt: `Analyze the following Git diff between master/main branch and feature branch, then generate comprehensive test cases.

**Branch Information:**
- Source Branch (feature): [FEATURE_BRANCH_NAME]
- Target Branch (base): [main/master]
- Related Ticket/Story: [JIRA-123 or description]

**Git Diff Output:**
\`\`\`diff
[PASTE YOUR GIT DIFF HERE]
(Use: git diff main...feature-branch)
\`\`\`

**Context:**
- Application/Module: [DESCRIBE THE APPLICATION AREA]
- Change Type: [NEW FEATURE / BUG FIX / REFACTORING / ENHANCEMENT]

Please analyze the diff and provide:

1. **Change Summary:**
   - List of modified files and their purpose
   - New functions/methods added
   - Modified functions/methods
   - Deleted code and its impact

2. **Impact Analysis:**
   - Direct impact areas
   - Indirect/related areas that might be affected
   - Integration points to consider
   - Potential regression areas

3. **Test Cases - Functional:**
   | ID | Test Case Title | Preconditions | Steps | Expected Result | Priority |
   |----|-----------------|---------------|-------|-----------------|----------|
   | TC001 | ... | ... | ... | ... | High/Medium/Low |

4. **Test Cases - Edge Cases:**
   - Boundary value tests
   - Null/empty input handling
   - Error scenarios
   - Timeout/performance scenarios

5. **Test Cases - Negative:**
   - Invalid input tests
   - Unauthorized access tests
   - Concurrent access tests
   - Data integrity tests

6. **Regression Test Recommendations:**
   - Existing tests that should be re-run
   - Areas requiring smoke testing
   - Integration tests needed

7. **Test Data Requirements:**
   - Required test data setup
   - Database state requirements
   - Mock/stub requirements

8. **Automation Recommendations:**
   - Which tests to automate
   - Suggested automation approach
   - API vs UI test recommendations`,
      tags: ['git', 'test cases', 'qa', 'code review', 'regression']
    },
    {
      id: 15,
      category: 'qa',
      title: 'API Test Automation (REST)',
      description: 'Generate REST API test scripts with assertions and validations',
      prompt: `Create comprehensive API test automation scripts for the following REST API endpoints.

**API Information:**
- Base URL: [BASE_URL]
- Authentication: [BEARER TOKEN / API KEY / BASIC AUTH / OAUTH2]
- Content-Type: [APPLICATION/JSON / XML / FORM-DATA]

**Endpoints to Test:**
\`\`\`
[PASTE YOUR API ENDPOINTS OR SWAGGER/OPENAPI SPEC]
Example:
POST /api/users - Create user
GET /api/users/{id} - Get user by ID
PUT /api/users/{id} - Update user
DELETE /api/users/{id} - Delete user
\`\`\`

**Test Framework:** [REST-ASSURED/POSTMAN/PYTEST/SUPERTEST/KARATE]
**Programming Language:** [JAVA/PYTHON/JAVASCRIPT]

Please provide:

1. **Test Structure:**
   - Base test class with common setup
   - Request/Response specification builders
   - Authentication helper methods

2. **Test Cases for Each Endpoint:**
   - Happy path tests (200, 201 responses)
   - Validation tests (400 Bad Request)
   - Authentication tests (401, 403)
   - Not found tests (404)
   - Server error handling (500)

3. **Assertions:**
   - Status code validation
   - Response body schema validation
   - Response time assertions
   - Header validations
   - Data type validations

4. **Data-Driven Tests:**
   - Parameterized test examples
   - Test data management approach

5. **Chained API Tests:**
   - Create -> Read -> Update -> Delete flow
   - Token extraction and reuse
   - Response data correlation

6. **Utility Methods:**
   - JSON path extraction
   - Response logging
   - Retry mechanism for flaky tests`,
      tags: ['api testing', 'rest', 'automation', 'qa', 'postman']
    },
    {
      id: 16,
      category: 'qa',
      title: 'Cypress/Playwright Test Generator',
      description: 'Generate modern E2E tests with Cypress or Playwright',
      prompt: `Generate end-to-end test automation code using modern testing framework.

**Framework:** [CYPRESS / PLAYWRIGHT]
**Language:** [JAVASCRIPT / TYPESCRIPT]

**Application Details:**
- URL: [APPLICATION_URL]
- Application Type: [SPA / MPA / SSR]

**Test Scenario:**
[DESCRIBE THE USER JOURNEY TO TEST]

**Page Elements:**
[LIST KEY ELEMENTS WITH THEIR SELECTORS OR DESCRIPTIONS]

Please provide:

1. **Test File Structure:**
   - Proper describe/it blocks
   - beforeEach/afterEach hooks
   - Test organization best practices

2. **Page Objects or Commands:**
   - Custom commands for reusable actions
   - Page object pattern implementation
   - Selector constants

3. **Test Implementation:**
   - Navigation and URL assertions
   - Element interactions (click, type, select)
   - Assertions and validations
   - Screenshot and video on failure

4. **Advanced Patterns:**
   - API mocking/stubbing
   - Network request interception
   - Fixtures for test data
   - Environment-based configuration

5. **CI/CD Integration:**
   - Configuration for headless mode
   - Parallel execution setup
   - Reporting configuration

6. **Handling Common Scenarios:**
   - Authentication/Login
   - File uploads
   - Iframes and popups
   - Dynamic content waits`,
      tags: ['cypress', 'playwright', 'e2e', 'automation', 'javascript']
    },
    {
      id: 17,
      category: 'qa',
      title: 'Bug Report Generator',
      description: 'Create detailed and professional bug reports',
      prompt: `Generate a comprehensive bug report based on the following information.

**Bug Summary:**
[BRIEF DESCRIPTION OF THE BUG]

**Environment:**
- Application Version: [VERSION]
- Browser/Device: [BROWSER AND VERSION / DEVICE MODEL]
- OS: [OPERATING SYSTEM]
- Environment: [DEV/QA/STAGING/PROD]

**Steps to Reproduce:**
[DESCRIBE WHAT YOU DID]

**Actual Result:**
[WHAT HAPPENED]

**Expected Result:**
[WHAT SHOULD HAVE HAPPENED]

**Additional Context:**
[ANY ERROR MESSAGES, LOGS, OR SCREENSHOTS DESCRIPTION]

Please generate a professional bug report with:

1. **Title:** Clear, concise, searchable title

2. **Description:** 
   - Detailed explanation of the issue
   - Business impact assessment
   - User impact description

3. **Reproduction Steps:**
   - Numbered, clear steps
   - Preconditions
   - Test data used

4. **Technical Details:**
   - Console errors (if any)
   - Network request failures
   - Relevant logs

5. **Classification:**
   - Severity: [BLOCKER/CRITICAL/MAJOR/MINOR/TRIVIAL]
   - Priority: [P1/P2/P3/P4]
   - Type: [FUNCTIONAL/UI/PERFORMANCE/SECURITY/USABILITY]

6. **Attachments Checklist:**
   - Screenshots needed
   - Video recording needed
   - Log files needed
   - HAR file needed

7. **Suggested Labels/Tags:**
   - Component affected
   - Sprint/Release
   - Related features`,
      tags: ['bug report', 'qa', 'jira', 'defect', 'documentation']
    },
    {
      id: 18,
      category: 'qa',
      title: 'Performance Test Scenario (k6/JMeter)',
      description: 'Create load and performance test scripts',
      prompt: `Create a performance/load test scenario for the following application.

**Application Details:**
- Target URL: [URL]
- Type: [WEB APP / API / MICROSERVICE]
- Expected Users: [CONCURRENT USERS]
- Peak Load: [REQUESTS PER SECOND]

**Test Tool:** [K6 / JMETER / GATLING / LOCUST]

**Endpoints to Test:**
\`\`\`
[LIST CRITICAL ENDPOINTS]
GET /api/products - Product listing
POST /api/orders - Create order
GET /api/users/profile - User profile
\`\`\`

**Test Requirements:**
- Test Duration: [MINUTES]
- Ramp-up Period: [SECONDS]
- Think Time: [SECONDS]

Please provide:

1. **Test Script:**
   - Complete executable script
   - Virtual user simulation
   - Realistic user journey
   - Think time implementation

2. **Load Patterns:**
   - Smoke test (minimal load)
   - Load test (expected load)
   - Stress test (beyond capacity)
   - Spike test (sudden increase)
   - Soak test (extended duration)

3. **Assertions & Thresholds:**
   - Response time thresholds (p95, p99)
   - Error rate thresholds
   - Throughput expectations
   - Custom checks

4. **Data Parameterization:**
   - CSV/JSON data files
   - Dynamic data generation
   - Correlation for dynamic values

5. **Monitoring & Metrics:**
   - Key metrics to capture
   - Grafana dashboard setup
   - InfluxDB integration

6. **Results Analysis Template:**
   - Performance baseline
   - Bottleneck identification
   - Recommendations format`,
      tags: ['performance', 'load testing', 'k6', 'jmeter', 'qa']
    },
    {
      id: 19,
      category: 'qa',
      title: 'Mobile App Test (Appium)',
      description: 'Generate Appium mobile automation test scripts',
      prompt: `Create mobile app automation test scripts using Appium.

**Application Details:**
- App Name: [APP_NAME]
- Platform: [ANDROID / IOS / BOTH]
- App Package/Bundle ID: [PACKAGE_NAME]
- App Activity (Android): [MAIN_ACTIVITY]

**Test Scenario:**
[DESCRIBE THE MOBILE USER FLOW TO AUTOMATE]

**Device Configuration:**
- Device: [REAL DEVICE / EMULATOR / SIMULATOR]
- OS Version: [VERSION]
- Device Name: [NAME]

**Programming Language:** [JAVA / PYTHON / JAVASCRIPT]
**Framework:** [TESTNG / PYTEST / MOCHA / WDIO]

Please provide:

1. **Desired Capabilities:**
   - Platform-specific capabilities
   - App installation settings
   - Device configuration

2. **Page Objects:**
   - Screen classes with locators
   - Mobile-specific locator strategies (accessibility id, xpath, etc.)
   - Gesture helper methods

3. **Test Cases:**
   - App launch and initialization
   - Navigation tests
   - Form interactions
   - Gesture handling (swipe, scroll, tap)

4. **Mobile-Specific Handling:**
   - Permission dialogs
   - Push notifications
   - Background/foreground switching
   - Network condition simulation
   - Device rotation

5. **Cross-Platform Strategy:**
   - Shared test logic
   - Platform-specific implementations
   - Screen abstraction layer

6. **CI/CD Integration:**
   - Cloud device farm setup (BrowserStack/Sauce Labs)
   - Parallel execution configuration
   - Report generation`,
      tags: ['appium', 'mobile', 'android', 'ios', 'automation']
    },
    {
      id: 20,
      category: 'qa',
      title: 'Test Strategy Document',
      description: 'Create comprehensive test strategy and plan documents',
      prompt: `Create a comprehensive Test Strategy document for the following project.

**Project Information:**
- Project Name: [PROJECT_NAME]
- Description: [BRIEF DESCRIPTION]
- Timeline: [START_DATE to END_DATE]
- Team Size: [NUMBER OF QA ENGINEERS]

**Application Scope:**
- Modules/Features: [LIST KEY FEATURES]
- Platforms: [WEB / MOBILE / API / DESKTOP]
- Integrations: [THIRD-PARTY SYSTEMS]

**Stakeholders:**
[LIST KEY STAKEHOLDERS]

Please create a Test Strategy document including:

1. **Introduction:**
   - Purpose and scope
   - Document conventions
   - References

2. **Test Objectives:**
   - Quality goals
   - Coverage targets
   - Performance benchmarks

3. **Test Scope:**
   - In-scope features
   - Out-of-scope items
   - Assumptions and constraints

4. **Test Approach:**
   - Testing types (Functional, Integration, E2E, etc.)
   - Testing levels (Unit, Component, System)
   - Manual vs Automated testing ratio
   - Exploratory testing approach

5. **Test Environment:**
   - Environment requirements
   - Test data strategy
   - Environment setup process

6. **Test Automation Strategy:**
   - Tools selection
   - Framework architecture
   - Automation scope and priorities
   - Maintenance approach

7. **Defect Management:**
   - Defect lifecycle
   - Severity and priority definitions
   - Bug triage process

8. **Risk Assessment:**
   - Identified risks
   - Mitigation strategies
   - Contingency plans

9. **Test Deliverables:**
   - Test cases
   - Test reports
   - Metrics and KPIs

10. **Entry/Exit Criteria:**
    - Test phase entry criteria
    - Test phase exit criteria
    - Go/No-Go decision factors`,
      tags: ['test strategy', 'documentation', 'qa', 'planning', 'management']
    },
    {
      id: 21,
      category: 'qa',
      title: 'Test Data Generator Script',
      description: 'Generate scripts for creating realistic test data',
      prompt: `Create test data generation scripts for the following data requirements.

**Data Requirements:**
- Data Type: [USER DATA / PRODUCT DATA / TRANSACTION DATA / etc.]
- Volume: [NUMBER OF RECORDS]
- Format: [JSON / CSV / SQL INSERT / XML]

**Data Schema:**
\`\`\`
[DESCRIBE YOUR DATA STRUCTURE]
Example:
User {
  id: UUID
  firstName: String
  lastName: String
  email: Email
  phone: Phone
  address: Address object
  dateOfBirth: Date
  createdAt: Timestamp
}
\`\`\`

**Constraints:**
- [LIST ANY DATA CONSTRAINTS OR BUSINESS RULES]
- Example: Age must be 18-65, Email must be unique

**Programming Language:** [PYTHON / JAVASCRIPT / JAVA]
**Libraries:** [FAKER / CHANCE / JAVAFAKER]

Please provide:

1. **Data Generator Script:**
   - Complete executable script
   - Realistic data using faker libraries
   - Proper data relationships

2. **Data Variations:**
   - Valid data (happy path)
   - Boundary values
   - Invalid data for negative testing
   - Edge cases

3. **Localization:**
   - Multi-language support
   - Country-specific formats
   - Currency variations

4. **Output Options:**
   - File output (JSON, CSV, SQL)
   - Database direct insert
   - API calls to seed data

5. **Reproducibility:**
   - Seed values for consistent data
   - Version control approach
   - Documentation

6. **Cleanup Scripts:**
   - Data teardown procedures
   - Database reset scripts`,
      tags: ['test data', 'faker', 'data generation', 'qa', 'automation']
    },
    {
      id: 22,
      category: 'qa',
      title: 'CI/CD Pipeline for Tests',
      description: 'Create CI/CD pipeline configuration for test automation',
      prompt: `Create a CI/CD pipeline configuration for running automated tests.

**CI/CD Platform:** [GITHUB ACTIONS / GITLAB CI / JENKINS / AZURE DEVOPS]

**Test Types to Run:**
- Unit Tests: [YES/NO]
- Integration Tests: [YES/NO]
- E2E Tests: [YES/NO]
- API Tests: [YES/NO]
- Performance Tests: [YES/NO]

**Test Framework:** [JEST / PYTEST / TESTNG / CYPRESS / PLAYWRIGHT]
**Programming Language:** [JAVASCRIPT / PYTHON / JAVA]

**Requirements:**
- Parallel execution: [YES/NO]
- Multiple browsers: [LIST BROWSERS]
- Test environments: [DEV/QA/STAGING]
- Notifications: [SLACK / EMAIL / TEAMS]

Please provide:

1. **Pipeline Configuration File:**
   - Complete YAML/Groovy configuration
   - Stage definitions
   - Job dependencies

2. **Pipeline Stages:**
   - Build stage
   - Unit test stage
   - Integration test stage
   - E2E test stage
   - Report generation stage

3. **Environment Setup:**
   - Docker container usage
   - Browser installation
   - Dependency caching

4. **Test Execution:**
   - Parallel test execution
   - Test splitting strategies
   - Retry mechanisms for flaky tests

5. **Reporting & Artifacts:**
   - Test report generation
   - Coverage reports
   - Screenshot/video artifacts
   - Report publishing

6. **Notifications:**
   - Success/failure notifications
   - Test summary in PR comments
   - Slack/Teams integration

7. **Scheduled Runs:**
   - Nightly regression runs
   - Smoke test schedules
   - Performance test schedules`,
      tags: ['ci/cd', 'pipeline', 'github actions', 'jenkins', 'automation']
    },
    {
      id: 1,
      category: 'coding',
      title: 'Code Review Assistant',
      description: 'Get detailed code reviews with suggestions for improvement',
      prompt: `Please review the following code and provide:
1. A summary of what the code does
2. Potential bugs or issues
3. Performance optimization suggestions
4. Code style and best practice recommendations
5. Security considerations if applicable

Code to review:
[PASTE YOUR CODE HERE]`,
      tags: ['code review', 'debugging', 'optimization']
    },
    {
      id: 2,
      category: 'coding',
      title: 'API Documentation Generator',
      description: 'Generate comprehensive API documentation from code',
      prompt: `Generate comprehensive API documentation for the following endpoint/function. Include:
1. Endpoint/Function description
2. Parameters with types and descriptions
3. Request/Response examples
4. Error codes and handling
5. Usage examples in multiple programming languages

Code/Endpoint:
[PASTE YOUR CODE OR ENDPOINT INFO HERE]`,
      tags: ['documentation', 'api', 'technical writing']
    },
    {
      id: 3,
      category: 'writing',
      title: 'Blog Post Outline Creator',
      description: 'Create structured outlines for blog posts',
      prompt: `Create a detailed blog post outline for the following topic. Include:
1. Engaging title options (3-5 variations)
2. Meta description (155 characters)
3. Introduction hook
4. Main sections with subheadings
5. Key points for each section
6. Call-to-action ideas
7. SEO keywords to target

Topic: [YOUR TOPIC HERE]
Target audience: [YOUR AUDIENCE]
Desired word count: [WORD COUNT]`,
      tags: ['content writing', 'seo', 'blogging']
    },
    {
      id: 4,
      category: 'analysis',
      title: 'Data Analysis Prompt',
      description: 'Analyze datasets and extract insights',
      prompt: `Analyze the following data and provide:
1. Summary statistics
2. Key trends and patterns
3. Anomalies or outliers
4. Correlations between variables
5. Actionable insights and recommendations
6. Visualization suggestions

Data:
[PASTE YOUR DATA HERE]

Context: [DESCRIBE THE CONTEXT/BUSINESS PROBLEM]`,
      tags: ['data analysis', 'statistics', 'insights']
    },
    {
      id: 5,
      category: 'creative',
      title: 'Character Creator',
      description: 'Design detailed characters for stories or games',
      prompt: `Create a detailed character profile including:
1. Name and aliases
2. Physical appearance (age, height, build, distinctive features)
3. Personality traits (strengths and flaws)
4. Background and origin story
5. Motivations and goals
6. Skills and abilities
7. Relationships and connections
8. Secrets and internal conflicts
9. Character arc potential

Genre/Setting: [YOUR GENRE/SETTING]
Role in story: [PROTAGONIST/ANTAGONIST/SUPPORTING]`,
      tags: ['character design', 'storytelling', 'worldbuilding']
    },
    {
      id: 6,
      category: 'business',
      title: 'SWOT Analysis Template',
      description: 'Conduct comprehensive SWOT analysis',
      prompt: `Conduct a comprehensive SWOT analysis for:

Company/Product: [NAME]
Industry: [INDUSTRY]
Context: [CURRENT SITUATION]

Please provide:

**STRENGTHS** (Internal positive factors)
- List 5-7 key strengths with explanations

**WEAKNESSES** (Internal negative factors)
- List 5-7 key weaknesses with explanations

**OPPORTUNITIES** (External positive factors)
- List 5-7 opportunities with potential impact

**THREATS** (External negative factors)
- List 5-7 threats with risk assessment

**STRATEGIC RECOMMENDATIONS**
- Based on the analysis, provide 3-5 strategic recommendations`,
      tags: ['business strategy', 'analysis', 'planning']
    },
    {
      id: 7,
      category: 'coding',
      title: 'Unit Test Generator',
      description: 'Generate comprehensive unit tests for your code',
      prompt: `Generate comprehensive unit tests for the following code. Include:
1. Test cases for all public methods
2. Edge cases and boundary conditions
3. Error handling scenarios
4. Mock objects where necessary
5. Setup and teardown procedures
6. Descriptive test names

Use testing framework: [JEST/PYTEST/JUNIT/etc.]

Code to test:
[PASTE YOUR CODE HERE]`,
      tags: ['testing', 'unit tests', 'quality assurance']
    },
    {
      id: 8,
      category: 'writing',
      title: 'Email Response Template',
      description: 'Craft professional email responses',
      prompt: `Write a professional email response for the following scenario:

Original email/context: [PASTE ORIGINAL EMAIL OR DESCRIBE SITUATION]
Desired tone: [FORMAL/FRIENDLY/APOLOGETIC/etc.]
Key points to address: [LIST KEY POINTS]
Desired outcome: [WHAT YOU WANT TO ACHIEVE]

Please provide:
1. Subject line (if new email)
2. Professional greeting
3. Clear and concise body
4. Appropriate sign-off
5. Alternative versions if needed`,
      tags: ['email', 'communication', 'professional']
    },
    {
      id: 9,
      category: 'analysis',
      title: 'Competitor Analysis',
      description: 'Analyze competitors in your market',
      prompt: `Conduct a detailed competitor analysis for:

Your Company: [YOUR COMPANY]
Competitors to analyze: [LIST COMPETITORS]
Industry: [INDUSTRY]

Please analyze:
1. **Market Positioning** - How each competitor positions themselves
2. **Product/Service Comparison** - Features, pricing, quality
3. **Marketing Strategies** - Channels, messaging, branding
4. **Strengths & Weaknesses** - Of each competitor
5. **Customer Reviews Summary** - Sentiment analysis
6. **Opportunities** - Gaps you can exploit
7. **Recommendations** - Strategic actions to take`,
      tags: ['competitive analysis', 'market research', 'strategy']
    },
    {
      id: 10,
      category: 'creative',
      title: 'Story Plot Generator',
      description: 'Generate engaging story plots and narratives',
      prompt: `Generate a compelling story plot with the following parameters:

Genre: [YOUR GENRE]
Setting: [TIME PERIOD AND LOCATION]
Theme: [CENTRAL THEME]
Tone: [DARK/LIGHT/HUMOROUS/etc.]

Please include:
1. **Premise** - One paragraph hook
2. **Main Characters** - Brief descriptions
3. **Plot Structure**
   - Inciting incident
   - Rising action (3-5 major plot points)
   - Climax
   - Falling action
   - Resolution
4. **Subplots** - 2-3 secondary storylines
5. **Plot Twists** - 2-3 unexpected turns
6. **Opening Scene** - First chapter outline`,
      tags: ['storytelling', 'plot development', 'creative writing']
    },
    {
      id: 11,
      category: 'business',
      title: 'Meeting Agenda Creator',
      description: 'Create effective meeting agendas',
      prompt: `Create a professional meeting agenda for:

Meeting Purpose: [PURPOSE]
Duration: [TIME IN MINUTES]
Attendees: [LIST OF ATTENDEES AND ROLES]
Date/Time: [WHEN]

Please include:
1. Meeting title and objectives
2. Time-boxed agenda items
3. Discussion topics with owners
4. Decision points required
5. Action items section
6. Follow-up and next steps
7. Pre-meeting preparation notes for attendees`,
      tags: ['meetings', 'productivity', 'management']
    },
    {
      id: 12,
      category: 'coding',
      title: 'Regex Pattern Builder',
      description: 'Build and explain regular expressions',
      prompt: `Create a regular expression pattern for the following requirement:

What to match: [DESCRIBE WHAT YOU WANT TO MATCH]
Examples that should match: [PROVIDE EXAMPLES]
Examples that should NOT match: [PROVIDE COUNTER-EXAMPLES]
Programming language: [LANGUAGE]

Please provide:
1. The regex pattern
2. Step-by-step explanation of each part
3. Test cases with expected results
4. Common edge cases to consider
5. Alternative patterns if applicable
6. Performance considerations`,
      tags: ['regex', 'pattern matching', 'text processing']
    }
  ];

  const filteredPrompts = prompts.filter(prompt => {
    const matchesCategory = selectedCategory === 'all' || prompt.category === selectedCategory;
    const matchesSearch = searchTerm === '' || 
      prompt.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prompt.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prompt.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const handleCopy = (id, text) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "AI Template Prompts - Prompt Library",
    "url": `${SITE_DOMAIN}/template-prompts`,
    "applicationCategory": "DeveloperApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": "A curated collection of AI prompt templates for coding, writing, analysis, creative work, and business tasks."
  };

  return (
    <>
      <SEO 
        title="AI Template Prompts - Ready-to-Use Prompt Library | yourjsontools.com"
        description="A curated collection of AI prompt templates for coding, writing, analysis, creative work, and business tasks. Copy and customize ready-made prompts for ChatGPT, Claude, and other AI assistants."
        keywords={["ai prompts", "prompt templates", "chatgpt prompts", "prompt engineering", "ai templates", "prompt library"]}
        canonicalUrl={`${SITE_DOMAIN}/template-prompts`}
        structuredData={jsonLd}
      />
      
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px 20px 0' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>AI Template Prompts</h1>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Ready-to-use prompt templates for ChatGPT, Claude, and other AI assistants. Click to copy and customize.
        </p>
      </div>
      
      {/* Search and Filter */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
        <div style={{ marginBottom: '20px' }}>
          <input
            type="text"
            placeholder="Search prompts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '16px',
              border: '1px solid #ccc',
              borderRadius: '8px',
              boxSizing: 'border-box'
            }}
          />
        </div>
        
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '30px' }}>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              style={{
                padding: '8px 16px',
                border: selectedCategory === cat.id ? '2px solid #1976D2' : '1px solid #ccc',
                borderRadius: '20px',
                background: selectedCategory === cat.id ? '#e3f2fd' : '#fff',
                color: selectedCategory === cat.id ? '#1976D2' : '#333',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: selectedCategory === cat.id ? 600 : 400,
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <span>{cat.icon}</span>
              <span>{cat.name}</span>
            </button>
          ))}
        </div>
      </div>
      
      {/* Prompts Grid */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '20px',
          marginBottom: '30px'
        }}>
          {filteredPrompts.map(prompt => (
            <div 
              key={prompt.id} 
              style={{
                background: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: '12px',
                overflow: 'hidden',
                transition: 'box-shadow 0.2s ease'
              }}
            >
              {/* Card Header */}
              <div style={{ padding: '20px', borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '20px' }}>
                    {categories.find(c => c.id === prompt.category)?.icon}
                  </span>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#333' }}>
                    {prompt.title}
                  </h3>
                </div>
                <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#666' }}>
                  {prompt.description}
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {prompt.tags.map((tag, idx) => (
                    <span 
                      key={idx} 
                      style={{
                        background: '#f0f0f0',
                        color: '#666',
                        padding: '3px 10px',
                        borderRadius: '12px',
                        fontSize: '12px'
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              
              {/* Prompt Content */}
              <div 
                style={{ 
                  padding: '15px 20px', 
                  background: '#fafafa',
                  maxHeight: expandedId === prompt.id ? 'none' : '120px',
                  overflow: 'hidden',
                  position: 'relative'
                }}
              >
                <pre style={{
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'Monaco, Menlo, monospace',
                  fontSize: '12px',
                  color: '#444',
                  lineHeight: '1.5'
                }}>
                  {prompt.prompt}
                </pre>
                {expandedId !== prompt.id && (
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '50px',
                    background: 'linear-gradient(transparent, #fafafa)',
                    pointerEvents: 'none'
                  }} />
                )}
              </div>
              
              {/* Card Actions */}
              <div style={{ 
                padding: '12px 20px', 
                display: 'flex', 
                gap: '10px',
                borderTop: '1px solid #f0f0f0'
              }}>
                <button
                  onClick={() => toggleExpand(prompt.id)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    background: '#fff',
                    color: '#333',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 500
                  }}
                >
                  {expandedId === prompt.id ? 'Show Less' : 'Show More'}
                </button>
                <button
                  onClick={() => handleCopy(prompt.id, prompt.prompt)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    border: 'none',
                    borderRadius: '6px',
                    background: copiedId === prompt.id ? '#4CAF50' : '#1976D2',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 500,
                    transition: 'background 0.2s ease'
                  }}
                >
                  {copiedId === prompt.id ? '✓ Copied!' : '📋 Copy'}
                </button>
              </div>
            </div>
          ))}
        </div>
        
        {filteredPrompts.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px',
            background: '#fff',
            borderRadius: '12px',
            marginBottom: '30px'
          }}>
            <p style={{ color: '#666', fontSize: '16px', margin: '0 0 15px 0' }}>
              No prompts found matching your criteria.
            </p>
            <button 
              onClick={() => { setSelectedCategory('all'); setSearchTerm(''); }}
              style={{
                padding: '10px 24px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                background: '#fff',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
      
      {/* How to Use Section */}
      <div style={{ maxWidth: '1200px', margin: '40px auto 20px', padding: '0 20px', fontSize: '15px', lineHeight: '1.6', color: '#444' }}>
        <h2 style={{ fontSize: '22px', marginBottom: '15px', color: '#333' }}>How to Use These Prompts</h2>
        
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '30px' }}>
          <div style={{ flex: 1, minWidth: '250px', background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e0e0e0' }}>
            <div style={{ fontSize: '24px', marginBottom: '10px' }}>1️⃣</div>
            <h3 style={{ fontSize: '16px', margin: '0 0 8px 0', color: '#333' }}>Browse & Select</h3>
            <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
              Find a prompt template that matches your needs using categories or search.
            </p>
          </div>
          <div style={{ flex: 1, minWidth: '250px', background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e0e0e0' }}>
            <div style={{ fontSize: '24px', marginBottom: '10px' }}>2️⃣</div>
            <h3 style={{ fontSize: '16px', margin: '0 0 8px 0', color: '#333' }}>Copy & Customize</h3>
            <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
              Copy the prompt and replace the placeholder text with your specific content.
            </p>
          </div>
          <div style={{ flex: 1, minWidth: '250px', background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e0e0e0' }}>
            <div style={{ fontSize: '24px', marginBottom: '10px' }}>3️⃣</div>
            <h3 style={{ fontSize: '16px', margin: '0 0 8px 0', color: '#333' }}>Use with AI</h3>
            <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
              Paste into ChatGPT, Claude, or your preferred AI assistant for great results.
            </p>
          </div>
        </div>
        
        <h2 style={{ fontSize: '22px', marginBottom: '15px', color: '#333' }}>About AI Prompt Templates</h2>
        <p style={{ marginBottom: '15px' }}>
          Effective prompts are the key to getting great results from AI assistants like ChatGPT, Claude, and others. 
          Our curated collection of prompt templates helps you:
        </p>
        <ul style={{ marginBottom: '15px', paddingLeft: '20px' }}>
          <li style={{ marginBottom: '8px' }}><strong>Save Time:</strong> Skip the trial-and-error of crafting prompts from scratch</li>
          <li style={{ marginBottom: '8px' }}><strong>Get Better Results:</strong> Use proven prompt structures that work</li>
          <li style={{ marginBottom: '8px' }}><strong>Learn Best Practices:</strong> See how to structure effective prompts</li>
          <li style={{ marginBottom: '8px' }}><strong>Increase Productivity:</strong> Automate repetitive tasks with consistent prompts</li>
        </ul>
        <p>
          All prompts are designed with clear placeholders (shown in [BRACKETS]) that you can easily replace 
          with your own content. Each prompt follows best practices for prompt engineering, including 
          clear instructions, structured output requests, and context setting.
        </p>
      </div>
    </>
  );
};

export default TemplatePromptsPage;
